import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';

import MediaQueryContainer from '../../containers/media-query-container/media-query-container';
import QueryParamManager from '../../containers/query-param-manager/query-param-manager';
import AuthenticationWrapped from '../../containers/authentication/authentication';

import {
  dataDependencyTypePicker,
  dataDependencyIdsAdder,
  stringInputSpec,
  stringSelectSpec,
  boolCheckboxSpec,
  repeatable,
  group,
  numberInputSpec,
} from '../../lib/editing-configs';
import { setBuilderVersion } from '../../containers/builder/actions';

const Page = ({
  className,
  children,
  history,
  queryParams,
  watchQueryParams,
  requiresAuth,
  authRoleRequired,
  loginModalCopyOverride,
  loginPageCopyOverride,
  loginTrkLocation,
  loginTrkFormat,
  dispatch,
  lpcVersion,
  // Branded Login Props
  useBrandedLogin,
  brandedBackgroundImage,
  brandedIcon,
  brandedTitle,
  brandedSubtitle,
  customCtaText,
  topLoginBoxColor,
  bottomLoginBoxColor,
  topLoginBoxMargin,
  bottomLoginBoxMargin,
}) => {
  React.useEffect(() => {
    if (lpcVersion) {
      dispatch(setBuilderVersion(lpcVersion));
    }
  }, [dispatch, lpcVersion]);
  return (
    <div className={className}>
      <MediaQueryContainer />
      <QueryParamManager
        watchQueryParams={watchQueryParams}
        queryParams={queryParams}
      />
      <AuthenticationWrapped
        requiresAuth={requiresAuth}
        authRoleRequired={authRoleRequired}
        loginModalCopyOverride={loginModalCopyOverride}
        loginPageCopyOverride={loginPageCopyOverride}
        loginTrkLocation={loginTrkLocation}
        loginTrkFormat={loginTrkFormat}
        onSuccess={history.location.pathname}
        // branded Login
        useBrandedLogin={useBrandedLogin}
        brandedBackgroundImage={brandedBackgroundImage}
        brandedIcon={brandedIcon}
        brandedTitle={brandedTitle}
        brandedSubtitle={brandedSubtitle}
        customCtaText={customCtaText}
        topLoginBoxColor={topLoginBoxColor}
        bottomLoginBoxColor={bottomLoginBoxColor}
        topLoginBoxMargin={topLoginBoxMargin}
        bottomLoginBoxMargin={bottomLoginBoxMargin}
      >
        {children}
      </AuthenticationWrapped>
    </div>
  );
};

Page.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
  history: PropTypes.object,
  queryParams: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  watchQueryParams: PropTypes.bool,
  includeInactiveOrNonMonetizingProducts: PropTypes.bool,
  requiresAuth: PropTypes.bool,
  authRoleRequired: PropTypes.string,
  loginModalCopyOverride: PropTypes.string,
  loginPageCopyOverride: PropTypes.string,
  loginTrkLocation: PropTypes.string,
  loginTrkFormat: PropTypes.string,
  dispatch: PropTypes.func,
  lpcVersion: PropTypes.string,
  // branded Login
  useBrandedLogin: PropTypes.bool,
  brandedBackgroundImage: PropTypes.string,
  brandedIcon: PropTypes.string,
  brandedTitle: PropTypes.string,
  brandedSubtitle: PropTypes.string,
  customCtaText: PropTypes.string,
  topLoginBoxColor: PropTypes.string,
  bottomLoginBoxColor: PropTypes.string,
  topLoginBoxMargin: PropTypes.number,
  bottomLoginBoxMargin: PropTypes.number,
};

Page.displayName = 'Page';

Page.defaultProps = {
  className: null,
  children: null,
  history: null,
  requiresAuth: false,
  useBrandedLogin: false,
  authRoleRequired: 'nerdwallet/logged_in',
};

Page.getEditingConfig = (componentDocument) => {
  const authRoleContent =
    componentDocument?.props?.requiresAuth === false
      ? []
      : [
          stringInputSpec(
            'authRoleRequired',
            'Role required for authentication',
            {
              help: 'The role that is required for authentication. Please note: if you choose to require authentication for a page, this defaults to "nerdwallet/logged_in", meaning the page will be configured to require users to login to view the page\'s contents. If the page requires a more targeted role for authentication, please specify here. Also, please note that all pages that require authentication can be viewed by NerdWallet employees (eg: users who possess the "nerdwallet/employee" role).',
            }
          ),
          stringInputSpec(
            'loginPageCopyOverride',
            'Login splash page custom copy',
            {
              help: "If you'd like to output custom copy on the login splash page, please locate your custom output in this field.",
            }
          ),
          stringInputSpec('loginModalCopyOverride', 'Custom login modal copy', {
            help: "If you'd like to output custom copy in the login modal, please locate your custom output in this field.",
          }),
        ];
  const authTrk =
    componentDocument?.props?.requiresAuth === false
      ? []
      : [
          stringInputSpec('loginTrkLocation', 'trk_location value', {
            help: 'Enter a value for trk_location for authentication and sign-up.',
          }),
          stringInputSpec('loginTrkFormat', 'trk_format value', {
            help: 'Enter a value for trk_format for authentication and sign-up.',
          }),
        ];
  const themedHeaderConfig = componentDocument?.meta?.needsGlobalNav
    ? [
        stringSelectSpec(
          'meta.theme',
          'Select the NerdWallet nav type',
          [
            { label: 'Default', value: 'default' },
            { label: 'Company', value: 'company' },
          ],
          {
            isProp: false,
            help: 'Note: requires a save to take effect.',
          }
        ),
      ]
    : [];

  const brandedLoginCustomBorderColors =
    componentDocument?.props?.useBrandedLogin &&
    componentDocument?.props?.customBorderColors
      ? [
          stringInputSpec('topLoginBoxColor', 'Top Login Box Color', {
            help: 'HexColor',
          }),
          stringInputSpec('bottomLoginBoxColor', 'Bottom Login Box Color', {
            help: 'HexColor',
          }),
        ]
      : [];

  const brandedLoginCustomMargins =
    componentDocument?.props?.useBrandedLogin &&
    componentDocument?.props?.customMargins
      ? [
          numberInputSpec('topLoginBoxMargin', 'Top Login Box Margin'),
          numberInputSpec('bottomLoginBoxMargin', 'Bottom Login BoxMargin'),
        ]
      : [];
  const brandedLoginInputOptions = componentDocument?.props?.useBrandedLogin
    ? [
        boolCheckboxSpec('useBrandedLogin', 'Use Branded Login'),
        stringInputSpec('brandedBackgroundImage', 'Branded Background Image'),
        stringInputSpec('brandedIcon', 'Branded Icon'),
        stringInputSpec('brandedSubtitle', 'Branded Subtitle'),
        stringInputSpec('customCtaText', 'Custom CTA Text'),
        boolCheckboxSpec('customBorderColors', 'Custom Border Colors'),
        ...brandedLoginCustomBorderColors,
        boolCheckboxSpec('customMargins', 'Custom Margin'),
        ...brandedLoginCustomMargins,
      ]
    : [boolCheckboxSpec('useBrandedLogin', 'Use Branded Login')];
  const brandedLoginInputConfig = componentDocument?.props?.requiresAuth
    ? [group('Branded Login', [...brandedLoginInputOptions])]
    : [];
  return [
    // Page meta info
    boolCheckboxSpec('meta.needsGlobalNav', 'Use NerdWallet nav', {
      isProp: false,
      help: 'Note: requires a save to take effect.',
    }),

    ...themedHeaderConfig,

    // Builder query params
    repeatable('queryParams', 'Current Query Params', 'Query param', () => [
      stringInputSpec('name', 'Query Param Name'),
      stringInputSpec('value', 'Query Param Value'),
    ]),
    boolCheckboxSpec('watchQueryParams', 'Filter components by query params?', {
      help: 'Some components may appear on the page only when certain query params are present. When this box is unchecked, all components will appear in the builder, regardless of whether the query param that makes them visible is present.',
    }),
    boolCheckboxSpec(
      'includeCompareCreditsCard',
      'Add compare credit cards feature?',
      {
        help: 'Some components may appear on the page only when certain query params are present. When this box is unchecked, all components will appear in the builder, regardless of whether the query param that makes them visible is present.',
      }
    ),
    componentDocument?.props?.includeCompareCreditsCard &&
      stringInputSpec(
        'compareCreditsCardEmptyLabel',
        'Add label to the empty state'
      ),

    // Data dependencies
    // This field is read from the JSON and passed to the providers on creation in client/routes/container.jsx.
    boolCheckboxSpec(
      'includeInactiveOrNonMonetizingProducts',
      'Include inactive or non-monetizing products on page?',
      {
        help: 'Products will appear on the page regardless of whether they are currently active on NerdWallet. Inactive products will come with messaging indicating them as such. You may need to save and refresh the page to see this change take effect.',
      }
    ),
    dataDependencyTypePicker,
    dataDependencyIdsAdder,
    stringInputSpec('dataDependency.data.slug', 'Slug', { isProp: false }),
    boolCheckboxSpec('requiresAuth', 'Page requires user authentication', {
      help: 'In order to view page, user must be signed in and have the required role/scope',
    }),
    ...brandedLoginInputConfig,
    ...authRoleContent,
    ...authTrk,
  ].filter(Boolean);
};

Page.isContainer = true;

export default compose(withRouter, connect())(Page);
